import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../api/client";
import UploadImage from "../common/UploadImage";

const asList = (payload, ...keys) => {
  if (Array.isArray(payload)) return payload;
  for (const k of keys) if (Array.isArray(payload?.[k])) return payload[k];
  return Array.isArray(payload?.data) ? payload.data : [];
};

const money = (n) => `$${Number(n || 0).toLocaleString()}`;

function formatDate(value) {
  if (!value) return "";
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });
}

function Icon({ d }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={d} />
    </svg>
  );
}

const APPROACH = [
  {
    title: "Assessment first",
    body: "Every family begins with a full assessment. Nobody is placed on a programme before we understand what they actually need.",
    icon: "M9 11l3 3L22 4M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11",
  },
  {
    title: "One coordinated team",
    body: "Therapists, educators and family-support staff share goals, so progress in one setting carries into the next.",
    icon: "M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm14 10v-2a4 4 0 0 0-3-3.87",
  },
  {
    title: "Families included",
    body: "Parents and carers are trained alongside the learner, because the hours outside our building matter most.",
    icon: "M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2M9 7a3 3 0 1 0 0 6 3 3 0 0 0 0-6Zm7.5 3.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z",
  },
  {
    title: "Measured openly",
    body: "Goals are written down, reviewed on a schedule and shared with families in plain language, not clinical shorthand.",
    icon: "M3 3v18h18M7 15l3-4 3 3 5-7",
  },
];

export default function HomePage() {
  const [programs, setPrograms] = useState(null);
  const [events, setEvents] = useState(null);
  const [team, setTeam] = useState(null);

  useEffect(() => {
    let cancelled = false;

    // Each section fails independently: a single unavailable endpoint should
    // hide that band, not blank the homepage.
    const load = async (path, keys, setter) => {
      try {
        const res = await api.get(path);
        if (!cancelled) setter(asList(res.data, ...keys));
      } catch {
        if (!cancelled) setter([]);
      }
    };

    load("/program/getAllprograms", ["programs"], setPrograms);
    load("/events/getallevents", ["events"], setEvents);
    load("/teammembers/Allemployees", ["employees"], setTeam);

    return () => { cancelled = true; };
  }, []);

  const now = Date.now();
  const upcoming = (events || [])
    .filter((e) => new Date(e.eventDate).getTime() >= now)
    .sort((a, b) => new Date(a.eventDate) - new Date(b.eventDate))
    .slice(0, 3);

  const featured = (programs || []).slice(0, 6);
  const enrolled = (programs || []).reduce((s, p) => s + (p.participantsCount || 0), 0);

  return (
    <>
      {/* ------------------------------------------------------------ hero */}
      <section className="es-hero">
        <div className="es-hero__inner">
          <div className="es-rise">
            <p className="es-hero__eyebrow">
              <span aria-hidden="true">●</span> Now accepting new assessments
            </p>

            <h1 className="es-hero__title">
              Specialist support that treats your child as{" "}
              <em>an individual</em>
            </h1>

            <p className="es-hero__lede">
              Speech and language therapy, occupational therapy, behaviour
              support and inclusive early years — delivered by one coordinated
              team, so families do not have to hold it together themselves.
            </p>

            <div className="es-hero__actions">
              <Link to="/program" className="es-btn es-hero__btn-light">
                Explore programs
              </Link>
              <Link to="/contact" className="es-btn es-hero__btn-outline">
                Book an assessment
              </Link>
            </div>
          </div>

          <div className="es-hero__panel es-rise es-rise-2">
            <div className="es-hero__stats">
              <div>
                <div className="es-hero__stat-value">
                  {programs ? programs.length : "—"}
                </div>
                <div className="es-hero__stat-label">Specialist programmes</div>
              </div>
              <div>
                <div className="es-hero__stat-value">{enrolled || "—"}</div>
                <div className="es-hero__stat-label">Current enrolments</div>
              </div>
              <div>
                <div className="es-hero__stat-value">
                  {team ? team.length : "—"}
                </div>
                <div className="es-hero__stat-label">Clinicians and educators</div>
              </div>
              <div>
                <div className="es-hero__stat-value">
                  {events ? upcoming.length : "—"}
                </div>
                <div className="es-hero__stat-label">Events coming up</div>
              </div>
            </div>

            <p style={{
              marginTop: "1.5rem",
              paddingTop: "1.25rem",
              borderTop: "1px solid rgb(255 255 255 / 0.16)",
              fontSize: "0.85rem",
              color: "#b6d0cd",
              lineHeight: 1.6,
            }}>
              Step-free access throughout, and a low-stimulation waiting room
              available on request.
            </p>
          </div>
        </div>
      </section>

      {/* -------------------------------------------------------- approach */}
      <section className="es-section">
        <div className="es-section__inner">
          <div className="es-section__head">
            <div>
              <p className="es-eyebrow">How we work</p>
              <h2 className="es-h2" style={{ marginBottom: "0.5rem" }}>
                Coordinated support, not a series of appointments
              </h2>
              <p className="es-body">
                Families arrive having already told their story a dozen times.
                We work as one team so that does not keep happening.
              </p>
            </div>
          </div>

          <div className="es-grid">
            {APPROACH.map((item) => (
              <article key={item.title} className="es-feature">
                <span className="es-feature__icon"><Icon d={item.icon} /></span>
                <h3 className="es-h3" style={{ margin: 0 }}>{item.title}</h3>
                <p className="es-body" style={{ fontSize: "0.93rem", margin: 0 }}>
                  {item.body}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* -------------------------------------------------------- programs */}
      <section className="es-section es-section--alt" aria-labelledby="home-programs">
        <div className="es-section__inner">
          <div className="es-section__head">
            <div>
              <p className="es-eyebrow">What we offer</p>
              <h2 className="es-h2" id="home-programs" style={{ marginBottom: 0 }}>
                Programmes running now
              </h2>
            </div>
            <Link to="/program" className="es-btn es-btn--ghost">View all programs</Link>
          </div>

          {programs === null ? (
            <div className="es-grid" aria-busy="true">
              {[0, 1, 2].map((i) => (
                <div key={i} className="es-card">
                  <div className="es-skeleton es-skeleton--card" />
                  <div className="es-card__body">
                    <div className="es-skeleton es-skeleton--text" style={{ width: "70%" }} />
                    <div className="es-skeleton es-skeleton--text" />
                  </div>
                </div>
              ))}
              <span className="es-visually-hidden">Loading programmes</span>
            </div>
          ) : featured.length === 0 ? (
            <div className="es-state">
              <p className="es-state__title">No programmes listed yet</p>
              <p>Check back shortly, or get in touch to discuss what you need.</p>
            </div>
          ) : (
            <div className="es-grid">
              {featured.map((p) => (
                <article key={p._id} className="es-card es-card--interactive">
                  <UploadImage file={p.image} alt="" className="es-media" />
                  <div className="es-card__body">
                    <h3 className="es-h3">{p.name}</h3>
                    <p className="es-body" style={{ fontSize: "0.92rem" }}>
                      {String(p.description || "").slice(0, 130)}
                      {String(p.description || "").length > 130 ? "…" : ""}
                    </p>
                    <p style={{
                      marginTop: "1rem",
                      fontWeight: 700,
                      color: "var(--es-teal-700)",
                      fontVariantNumeric: "tabular-nums",
                    }}>
                      {money(p.price)}
                      <span className="es-muted" style={{ fontWeight: 400 }}>
                        {" "}· {p.participantsCount || 0} enrolled
                      </span>
                    </p>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ---------------------------------------------------------- events */}
      {upcoming.length > 0 && (
        <section className="es-section" aria-labelledby="home-events">
          <div className="es-section__inner">
            <div className="es-section__head">
              <div>
                <p className="es-eyebrow">What&rsquo;s on</p>
                <h2 className="es-h2" id="home-events" style={{ marginBottom: 0 }}>
                  Upcoming events
                </h2>
              </div>
              <Link to="/events" className="es-btn es-btn--ghost">All events</Link>
            </div>

            <div className="es-grid">
              {upcoming.map((e) => (
                <article key={e._id} className="es-card es-card--interactive">
                  <UploadImage file={e.imageevents} alt="" className="es-media" />
                  <div className="es-card__body">
                    <span className="es-badge es-badge--neutral">{formatDate(e.eventDate)}</span>
                    <h3 className="es-h3" style={{ marginTop: "0.75rem" }}>{e.nameevents}</h3>
                    <p className="es-body" style={{ fontSize: "0.92rem" }}>
                      {String(e.description || "").slice(0, 120)}
                      {String(e.description || "").length > 120 ? "…" : ""}
                    </p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ------------------------------------------------------------ team */}
      {team && team.length > 0 && (
        <section className="es-section es-section--alt" aria-labelledby="home-team">
          <div className="es-section__inner">
            <div className="es-section__head">
              <div>
                <p className="es-eyebrow">Who you&rsquo;ll meet</p>
                <h2 className="es-h2" id="home-team" style={{ marginBottom: 0 }}>
                  Our team
                </h2>
              </div>
              <Link to="/about" className="es-btn es-btn--ghost">More about us</Link>
            </div>

            <div className="es-grid">
              {team.slice(0, 4).map((m) => (
                <article key={m._id} className="es-card">
                  <UploadImage
                    file={m.image}
                    alt={`Portrait of ${m.name}`}
                    className="es-media es-media--square"
                  />
                  <div className="es-card__body">
                    <h3 className="es-h3" style={{ marginBottom: "0.25rem" }}>{m.name}</h3>
                    <p className="es-muted" style={{ margin: 0 }}>{m.position}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ------------------------------------------------------------- cta */}
      <section className="es-section">
        <div className="es-section__inner">
          <div className="es-cta">
            <p className="es-eyebrow" style={{ color: "#7a4d1c", margin: 0 }}>Support our work</p>
            <h2>Fund a bursary place for a family who cannot afford one</h2>
            <p>
              Donations go directly to programme delivery. Every contribution
              helps a child access specialist therapy that would otherwise be
              out of reach.
            </p>
            <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
              <Link to="/donate" className="es-btn es-btn--primary">Make a donation</Link>
              <Link to="/contact" className="es-btn es-btn--ghost"
                    style={{ borderColor: "#8a5a2a", color: "#5a3a1c" }}>
                Talk to us first
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
